import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  FormHelperText,
  Grid,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
  Divider,
  Box
} from '@mui/material'
import { REQUIRED_FIELD, EMAIL_FIELD } from "../../../utils/constants";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import PhoneMask from "../../InputMask/PhoneMask";
import ReactGA from "react-ga4";
import CloseIcon from '@mui/icons-material/Close'
import CopyIcon from '@mui/icons-material/CopyAll';
import { saveLeadFromDrMonitora } from '../../../api/lead'

interface SignUpPayload {
  name: string;
  email: string;
  phone: string;
}

interface ContactCouponModalProps {
  isOpen: boolean
  onClose: () => void
}

const COUPON_CODE = 'DRMONITORA60'

export function ContactCouponModal({ isOpen = false, onClose }: ContactCouponModalProps) {
  const [showCouponCode, setShowCouponCode] = useState(false)
  const [isSendingData, setIsSendingData] = useState(false)

  const schemaSignup = yup.object({
    name: yup
      .string()
      .required(REQUIRED_FIELD)
      .min(2, 'Nome deve ter pelo menos 2 caracteres')
      .matches(
        /^[a-zA-ZÀ-ÿ\s]+$/,
        "Nome deve conter apenas letras e espaços"
      )
      .matches(
        /^\w+\s+\w+.*$/,
        "Por favor, informe seu nome completo (nome e sobrenome)"
      )
      .test('not-empty', 'Nome não pode ser apenas espaços', (value) => {
        return value ? value.trim().length > 0 : false;
      })
      .transform((value) => value?.trim()),
    email: yup
      .string()
      .required(REQUIRED_FIELD)
      .email(EMAIL_FIELD)
      .transform((value) => value?.toLowerCase().trim()),
    phone: yup.string().required(REQUIRED_FIELD),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: yupResolver(schemaSignup),
  });

  const onSubmitData = async (data: SignUpPayload) => {
    setIsSendingData(true)

    await saveLeadFromDrMonitora({ ...data, source: location.href })

    setIsSendingData(false)

    resetForm()
    setShowCouponCode(true)
  };

  const copyCouponCode = async () => {
    await navigator.clipboard.writeText(COUPON_CODE)
  }

  const resetForm = () => {
    reset()
    onClose()
  }

  return (
    <>
      <Dialog open={isOpen}>
        <Box display={'flex'} sx={{ justifyContent: 'space-between', alignItems: 'center', padding: '16px 4px 16px 22px' }}>
          <Typography variant="h3" color={"#000"}>
            Informe seus dados e resgate o cupom!
          </Typography>
          <Button onClick={() => resetForm()}
            sx={{
              color: '#000',
              padding: 0,
              '&:hover': {
                backgroundColor: 'transparent',
                color: '#333'
              }
            }}>
            <CloseIcon />
          </Button>
        </Box>
        <Divider />
        <DialogContent sx={{ padding: showCouponCode ? 0 : 3 }}>
          <form noValidate onSubmit={handleSubmit(onSubmitData)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <Controller
                    name="name"
                    control={control}
                    defaultValue={""}
                    render={({ field }) => (
                      <>
                        <InputLabel htmlFor="full_name">
                          Nome completo
                        </InputLabel>
                        <OutlinedInput
                          {...field}
                          id="name"
                          type="text"
                          name="name"
                          placeholder="Nome completo"
                          fullWidth
                          error={!!errors.name}
                        />
                        <FormHelperText sx={{ color: "#f44336" }}>
                          {errors.name?.message}
                        </FormHelperText>
                      </>
                    )}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <Controller
                    name="email"
                    control={control}
                    defaultValue={""}
                    render={({ field }) => (
                      <>
                        <InputLabel htmlFor="email">
                          E-mail
                        </InputLabel>
                        <OutlinedInput
                          {...field}
                          id="email"
                          type="email"
                          placeholder="Seu e-mail"
                          fullWidth
                          error={!!errors.email}
                        />
                        <FormHelperText sx={{ color: "#f44336" }}>
                          {errors.email?.message}
                        </FormHelperText>
                      </>
                    )}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <Controller
                    name="phone"
                    control={control}
                    defaultValue={""}
                    render={({ field }) => (
                      <>
                        <InputLabel htmlFor="phone">
                          Telefone
                        </InputLabel>
                        <OutlinedInput
                          {...field}
                          id="phone"
                          type="text"
                          placeholder="(xx) xxxxx-xxxx"
                          fullWidth
                          inputComponent={PhoneMask as any}
                          error={!!errors.phone}
                        />
                        <FormHelperText sx={{ color: "#f44336" }}>
                          {errors.phone?.message}
                        </FormHelperText>
                      </>
                    )}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Button
                  disableElevation
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    ReactGA.event("drmonitora_cta_submit", {
                      event: 'drmonitora_cta_submit',
                      cta_id: 'drmonitora_cta',
                      page_path: window.location.pathname
                    })
                  }}
                  disabled={isSendingData}
                >
                  {!isSendingData ? 'Enviar' : 'Enviando...'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showCouponCode}>
        <Box display={'flex'} sx={{ backgroundColor: '#fff', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 10px 16px 25px' }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            paddingTop: 3,
            width: '100%'
          }}>
            <Typography variant="h3" color={"#000"}>
              Obrigado por se cadastrar!
            </Typography>
            <Typography variant="h4" color={"#000"} sx={{ marginTop: '5px' }}>
              Seu desconto exclusivo já pode ser usado.
            </Typography>
          </Box>

          <Button onClick={() => setShowCouponCode(false)}
            sx={{
              color: '#000',
              minWidth: 'auto',
              padding: 0,
              '&:hover': {
                backgroundColor: 'transparent',
                color: '#333'
              }
            }}>
            <CloseIcon />
          </Button>
        </Box>
        <DialogContent sx={{ padding: 0 }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#f5f5f5',
            padding: 3
          }}>
            <Typography component={'span'} sx={{
              fontWeight: 400,
              fontSize: '15px',
              letterSpacing: '.005em',
              textIndent: 0,
              textAlign: 'center',
              marginBottom: '16px',
              color: '#1f1f1f'
            }}>
              Copie e cole o código direto no aplicativo da WashPass
            </Typography>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box sx={{
                display: 'flex',
                padding: '0 10px',
                border: '1px dashed #949494',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '6px',
              }}>
                <Typography component='span'
                  sx={{
                    fontWeight: 600,
                    fontSize: '15px',
                    letterSpacing: '.005em',
                    padding: '9px 35px'
                  }}>
                  {COUPON_CODE}
                </Typography>
                <Button onClick={copyCouponCode}
                  sx={{
                    padding: 0,
                    width: 'auto',
                    minWidth: 'auto',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: '#333'
                    }
                  }}>
                  <CopyIcon sx={{ color: '#000' }} />
                </Button>
              </Box>

              <Button fullWidth
                variant='outlined'
                href="/app-download"
                onClick={copyCouponCode}
                sx={{
                  fontWeight: 600,
                  backgroundColor: 'transparent',
                  color: '#06b6d4',
                  textTransform: 'none',
                  borderRadius: '6px',
                  borderColor: '#06b6d4',
                  '&:hover': {
                    backgroundColor: '#06b6d4',
                    color: 'white'
                  },
                  transition: 'all 0.3s ease'
                }}>
                Copiar e ir pro app
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  )
}